import React, { useEffect, useState } from 'react';
import { Card, Container, Row } from 'react-bootstrap';
import { CardBody } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { Btn } from '../../AbstractElements';
import { useGetReferralLogsQuery, useLazyGetReferralLogsQuery } from '../../features/referralApiSlice';

const REWARD_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'applied_to_subscription', label: 'Applied to subscription' },
  { value: 'applied', label: 'Applied' },
  { value: 'failed', label: 'Failed' },
];

const STATUS_BADGE_CLASSES = {
  pending: 'badge badge-light-secondary rounded-pill px-2 py-1',
  applied_to_subscription: 'badge badge-light-primary rounded-pill px-2 py-1',
  applied: 'badge badge-light-success rounded-pill px-2 py-1',
  failed: 'badge badge-light-danger rounded-pill px-2 py-1',
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

const formatMoney = (amount, currency) => {
  const numericAmount = Number(amount || 0);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) return '—';

  return `${numericAmount.toFixed(2)} ${currency || 'EUR'}`;
};

const formatPerson = (name, email, userId) => {
  const label = [name, email].filter(Boolean).join(' - ');
  return label || (userId ? `User #${userId}` : '—');
};

const convertJSONToCSV = (jsonData, columnHeaders) => {
  if (jsonData.length === 0) return '';

  const headers = `${columnHeaders.join(',')}\n`;
  const rows = jsonData
    .map((row) =>
      columnHeaders
        .map((field) => {
          const value = row[field] || '';
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join(','),
    )
    .join('\n');

  return headers + rows;
};

const ReferralLogs = () => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [rewardStatus, setRewardStatus] = useState('');
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: referralLogs,
    isLoading,
    isSuccess,
    refetch,
  } = useGetReferralLogsQuery({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearchTerm,
    reward_status: rewardStatus,
  });

  const [getAllReferralLogs] = useLazyGetReferralLogsQuery();

  useEffect(() => {
    refetch();
  }, [refetch, currentPage, pageSize, debouncedSearchTerm, rewardStatus]);

  useEffect(() => {
    if (!isSuccess || !referralLogs) return;

    const logsList = referralLogs?.data || [];
    setData(Array.isArray(logsList) ? logsList : []);
    setTotalRows(referralLogs?.pagination?.total || 0);
  }, [referralLogs, isSuccess]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRewardStatusChange = (event) => {
    setRewardStatus(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
  };

  const downloadReferralLogsCSV = async () => {
    try {
      setIsDownloadingCsv(true);
      const loadingToast = toast.loading('Fetching referral logs...');

      const result = await getAllReferralLogs({
        page: 1,
        limit: 10000,
        search: debouncedSearchTerm,
        reward_status: rewardStatus,
      }).unwrap();
      const allReferralLogs = result?.data || [];

      if (allReferralLogs.length === 0) {
        toast.dismiss(loadingToast);
        toast.error('No referral logs to download');
        return;
      }

      const csvHeaders = [
        'id',
        'applied_at',
        'reward_status',
        'referral_code',
        'referral_code_status',
        'customer_referrer_id',
        'referrer_name',
        'referrer_email',
        'customer_referee_id',
        'referee_name',
        'referee_email',
        'order_referee_id',
        'order_total',
        'order_product_name',
        'order_product_type',
        'reward_amount',
        'currency',
        'stripe_invoice_id',
        'applied_subscription_id',
      ];
      const csvData = allReferralLogs.map((log) => ({
        id: log.id || '',
        applied_at: log.applied_at ? new Date(log.applied_at).toLocaleString() : '',
        reward_status: log.reward_status || '',
        referral_code: log.referral_code || '',
        referral_code_status: log.referral_code_status || '',
        customer_referrer_id: log.customer_referrer_id || '',
        referrer_name: log.referrer_name || '',
        referrer_email: log.referrer_email || '',
        customer_referee_id: log.customer_referee_id || '',
        referee_name: log.referee_name || '',
        referee_email: log.referee_email || '',
        order_referee_id: log.order_referee_id || '',
        order_total: log.order_total || '',
        order_product_name: log.order_product_name || '',
        order_product_type: log.order_product_type || '',
        reward_amount: log.reward_amount || '',
        currency: log.currency || '',
        stripe_invoice_id: log.stripe_invoice_id || '',
        applied_subscription_id: log.applied_subscription_id || '',
      }));
      const csvContent = convertJSONToCSV(csvData, csvHeaders);

      if (!csvContent) {
        toast.dismiss(loadingToast);
        toast.error('No data to download');
        return;
      }

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `ReferralLogs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss(loadingToast);
      toast.success(`Successfully downloaded ${allReferralLogs.length} referral log(s)`);
    } catch (error) {
      console.error('Error downloading referral logs CSV:', error);
      toast.error('Error generating CSV. Please try again.');
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const tableColumns = [
    {
      name: 'Id',
      selector: (row) => `${row?.id || ''}`,
      width: '80px',
      sortable: true,
      style: { justifyContent: 'flex-start' },
    },
    {
      name: 'Applied At',
      selector: (row) => (row?.applied_at ? new Date(row.applied_at) : ''),
      sortable: true,
      center: false,
      cell: (row) => formatDate(row?.applied_at),
      width: '180px',
      style: { justifyContent: 'flex-start' },
    },
    {
      name: 'Reward Status',
      center: false,
      cell: (row) => (
        <div className="text-start w-100">
          <span
            className={
              STATUS_BADGE_CLASSES[row?.reward_status] || 'badge badge-light-secondary rounded-pill px-2 py-1'
            }
          >
            {row?.reward_status || '-'}
          </span>
        </div>
      ),
      width: '190px',
      style: { justifyContent: 'flex-start', textTransform: 'none' },
    },
    {
      name: 'Referral Code',
      selector: (row) => row?.referral_code || '—',
      sortable: true,
      center: false,
      width: '150px',
      style: { justifyContent: 'flex-start' },
    },
    {
      name: 'Referrer',
      selector: (row) => row?.referrer_email,
      sortable: false,
      center: false,
      wrap: true,
      width: '280px',
      style: { justifyContent: 'flex-start', textTransform: 'none' },
    },
    {
      name: 'Referee',
      selector: (row) => row?.referee_email,
      sortable: false,
      center: false,
      wrap: true,
      width: '280px',
      style: { justifyContent: 'flex-start', textTransform: 'none' },
    },
    {
      name: 'Order ID',
      selector: (row) => `${row?.order_referee_id || ''}`,
      sortable: true,
      center: false,
      width: '110px',
      style: { justifyContent: 'flex-start' },
    },
    {
      name: 'Reward',
      selector: (row) => formatMoney(row?.reward_amount, row?.currency),
      sortable: false,
      center: false,
      width: '120px',
      style: { justifyContent: 'flex-start' },
    },
  ];

  if (isLoading) return <div>Loading...</div>;

  return (
    <>
      <h1 className="t-text-xl t-font-bold t-mb-2 t-ml-2 md:t-ml-3 t-text-secondary">Referral Logs</h1>
      <Container fluid>
        <Card>
          <CardBody>
            <Row className="mb-3">
              <div className="col-6">
                <input
                  type="text"
                  placeholder="Search referral logs..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="form-control"
                />
              </div>
              <div className="col-3">
                <select className="form-select" value={rewardStatus} onChange={handleRewardStatusChange}>
                  {REWARD_STATUS_OPTIONS.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-3 text-end">
                <Btn
                  attrBtn={{
                    color: 'primary',
                    disabled: isDownloadingCsv || isLoading,
                    onClick: downloadReferralLogsCSV,
                  }}
                >
                  {isDownloadingCsv ? 'Downloading...' : 'Download CSV'}
                </Btn>
              </div>
            </Row>

            {isLoading && (
              <div className="text-center py-3 mt-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading referral logs...</p>
              </div>
            )}

            {!isLoading && (
              <div className="mb-3 text-muted mt-3">
                Showing {data.length} of {totalRows} referral logs
                {searchTerm && ` (filtered by "${searchTerm}")`}
              </div>
            )}

            <DataTable
              columns={tableColumns}
              data={data}
              striped={true}
              center={false}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              progressPending={isLoading}
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              paginationComponentOptions={{
                noRowsPerPage: false,
              }}
            />
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default ReferralLogs;
